import { useRef, useState } from 'react';

/** Rows per page for the contact / agent / letter typeaheads. */
export const OPTION_PAGE = 20;

/**
 * Paged server search behind a SheetSelect.
 *
 * Every party picker in this app is a CALLER-SCOPED server search — the agent sees
 * their own and their downline's contacts, never the whole registry — so one capped
 * page silently hid real matches: the sheet stopped at 20 rows and scrolling to the
 * bottom did nothing. This owns the page cursor: a new term re-primes at page 1,
 * scrolling the sheet appends the next page, and a short page ends the feed.
 *
 * Wire it to a SheetSelect as:
 *   onSearch={lookup.search} onLoadMore={lookup.loadMore}
 *   hasMore={lookup.hasMore} loadingMore={lookup.loadingMore} options={lookup.options}
 *
 * `load(term, page)` resolves to option rows. Nothing is memoised on purpose: the
 * caller's closure (which parties are already picked, which co-agency a letter list
 * belongs to) has to be the current one on every call.
 *
 * `load` must return the page as the server sent it — rows that are already picked
 * belong in it, greyed via markPicked, not filtered out. A filtered page arrives
 * short, and a short page is how this hook knows the feed has ended.
 */
export default function usePagedOptions(load, pageSize = OPTION_PAGE) {
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const term = useRef('');
  const page = useRef(1);
  const busy = useRef(false);
  // A stale page landing after a newer search must not be appended to the new list.
  const gen = useRef(0);

  const run = async (q, p, replace) => {
    if (busy.current && !replace) return;
    busy.current = true;
    const g = ++gen.current;
    if (!replace) setLoadingMore(true);
    try {
      const rows = await load(q, p);
      if (g !== gen.current) return;
      setOptions((prev) => (replace
        ? rows
        : [...prev, ...rows.filter((r) => !prev.some((x) => x.value === r.value))]));
      page.current = p;
      setHasMore(rows.length >= pageSize);
    } catch {
      if (g === gen.current) setHasMore(false);
    } finally {
      busy.current = false;
      if (g === gen.current) setLoadingMore(false);
    }
  };

  return {
    options,
    hasMore,
    loadingMore,
    search: (q) => { term.current = q; run(q, 1, true); },
    loadMore: () => run(term.current, page.current + 1, false),
  };
}
